import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import {
  getCountryCode,
  getLocale,
  getZone,
} from 'src/core/config/globalStates';
import { PAGE_SIZE } from 'src/core/domain/constants';
import {
  ContentValidationError,
  ImageUploadError,
} from 'src/core/domain/errors';
import { DeleteError } from 'src/core/domain/errors/deleteErrors';
import { ReportError } from 'src/core/domain/errors/reportErrors';
import {
  type SetSeekMaxThreadLikedStatusMutationVariables,
  SetSeekMaxThreadLikedStatusDocument,
  type SetSeekMaxCommentLikedStatusMutationVariables,
  SetSeekMaxCommentLikedStatusDocument,
  type CreateSeekMaxCommentMutationVariables,
  CreateSeekMaxCommentDocument,
  CreateSeekMaxReplyDocument,
  type CreateSeekMaxReplyMutationVariables,
  type DeleteSeekMaxCommentMutation,
  DeleteSeekMaxCommentDocument,
  type DeleteSeekMaxCommentMutationVariables,
  type ReportSeekMaxCommentMutation,
  ReportSeekMaxCommentDocument,
  type ReportSeekMaxCommentMutationVariables,
  type ReportSeekMaxThreadMutation,
  ReportSeekMaxThreadDocument,
  type ReportSeekMaxThreadMutationVariables,
  type DeleteSeekMaxThreadMutationVariables,
  DeleteSeekMaxThreadDocument,
  type DeleteSeekMaxThreadMutation,
  SeekMaxThreadAttachmentStatusDocument,
  type SeekMaxThreadAttachmentStatusQueryVariables,
  SeekMaxRepliesDocument,
  type SeekMaxRepliesQuery,
  type SeekMaxRepliesQueryVariables,
  DeleteSeekMaxReplyDocument,
  type DeleteSeekMaxReplyMutation,
  type DeleteSeekMaxReplyMutationVariables,
  ReportSeekMaxReplyDocument,
  type ReportSeekMaxReplyMutation,
  type ReportSeekMaxReplyMutationVariables,
  type SetSeekMaxThreadPollStatusMutationVariables,
  SetSeekMaxThreadPollStatusDocument,
} from 'src/generated/gql/graphql';
import {
  generateCommentLike,
  generateThreadLike,
  type CommentLike,
  type ThreadLike,
} from 'src/mapper/interactionMapper';
import { generateReplies, type Reply } from 'src/mapper/threadMapper';
import { client as csrClient } from 'src/modules/apollo/apolloClient';
import logger from 'src/modules/logger/logger';
import type { CareerHubZones, ConfigStateContext } from 'src/types';
import type { CommentReplyStatus } from 'src/types/comments';
import type { ThreadAttachment } from 'src/types/threadAttachment';

export interface updateThreadLikesParams {
  id: string;
  liked: boolean;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const updateThreadLikes = async ({
  id,
  liked,
  client = csrClient,
}: updateThreadLikesParams): Promise<ThreadLike> => {
  try {
    const variables: SetSeekMaxThreadLikedStatusMutationVariables = {
      input: {
        id,
        liked,
        zone: getZone(),
      },
      locale: getLocale(),
    };

    logger.info('updateThreadLikes', variables);

    const { data } = await client.mutate({
      mutation: SetSeekMaxThreadLikedStatusDocument,
      variables,
    });

    // This mutation returns a union type of SeekMaxThreadLikedStatusFailure or SeekMaxThreadLikedStatusSuccess, so we need to handle the failure state here
    const likeStatus = data?.setSeekMaxThreadLikedStatus;
    if (likeStatus?.__typename === 'SeekMaxThreadLikedStatusFailure') {
      throw likeStatus.errors;
    }
    return generateThreadLike(data);
  } catch (error) {
    logger.error({ error }, 'updateThreadLikes');
    throw error;
  }
};

export interface updateCommentLikesParams {
  id: string;
  liked: boolean;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const updateCommentLikes = async ({
  id,
  liked,
  client = csrClient,
}: updateCommentLikesParams): Promise<CommentLike> => {
  try {
    const variables: SetSeekMaxCommentLikedStatusMutationVariables = {
      input: {
        id,
        liked,
        zone: getZone(),
      },
      locale: getLocale(),
    };

    logger.info('updateCommentLikes', variables);

    const { data } = await client.mutate({
      mutation: SetSeekMaxCommentLikedStatusDocument,
      variables,
    });

    const likeStatus = data?.setSeekMaxCommentLikedStatus;
    if (likeStatus?.__typename === 'SeekMaxCommentLikedStatusFailure') {
      throw likeStatus.errors;
    }
    return generateCommentLike(data);
  } catch (error) {
    logger.error({ error }, 'updateCommentLikes');
    throw error;
  }
};

export interface createThreadCommentParams {
  threadId: string;
  content: string;
  client?: ApolloClient<NormalizedCacheObject>;
}

interface CreateThreadCommentResult {
  error: ContentValidationError | Error | null;
  id?: string;
}

export const createThreadComment = async ({
  threadId,
  content,
  client = csrClient,
}: createThreadCommentParams): Promise<CreateThreadCommentResult> => {
  try {
    const variables: CreateSeekMaxCommentMutationVariables = {
      zone: getZone(),
      locale: getLocale(),
      content,
      threadId,
    };

    logger.info('createThreadComment', variables);

    const { data } = await client.mutate({
      mutation: CreateSeekMaxCommentDocument,
      variables,
    });

    const result = data?.createSeekMaxComment;
    if (result?.__typename === 'CreateSeekMaxCommentSuccess') {
      return { error: null, id: result.id };
    }
    if (result?.__typename === 'CreateSeekMaxCommentFailure') {
      switch (result.errors[0].__typename) {
        case 'CreateSeekMaxCommentBlockedWordsError':
          return { error: new ContentValidationError('BLACKLISTED_WORDS') };
        case 'CreateSeekMaxCommentExceedRateLimitError':
          return { error: new ContentValidationError('RATE_LIMIT_EXCEEDED') };
        case 'CreateSeekMaxCommentUserIsBannedError':
          return { error: new ContentValidationError('UNAUTHORIZED_REPLY') };
        default:
          return { error: new Error(`Can't create a new comment`) };
      }
    }
    if (result === undefined) {
      return { error: new Error(`Can't create a new comment`) };
    }
    return { error: null };
  } catch (error) {
    logger.error({ error }, 'createThreadComment');
    throw error;
  }
};

export interface createCommentReplyParams {
  threadId: string;
  commentId: string;
  content: string;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const createCommentReply = async ({
  commentId,
  content,
  client = csrClient,
}: createCommentReplyParams): Promise<CommentReplyStatus> => {
  try {
    const variables: CreateSeekMaxReplyMutationVariables = {
      zone: getZone(),
      locale: getLocale(),
      content,
      commentId,
    };

    logger.info('createCommentReply', variables);

    const { data } = await client.mutate({
      mutation: CreateSeekMaxReplyDocument,
      variables,
    });

    const result = data?.createSeekMaxReply;
    if (!result) {
      throw new ContentValidationError();
    }
    if (result?.__typename === 'CreateSeekMaxReplyFailure') {
      let error: ContentValidationError | Error;
      switch (result.errors[0].__typename) {
        case 'CreateSeekMaxReplyBlockedWordsError':
          error = new ContentValidationError('BLACKLISTED_WORDS');
          break;
        case 'CreateSeekMaxReplyExceedRateLimitError':
          error = new ContentValidationError('RATE_LIMIT_EXCEEDED');
          break;
        case 'CreateSeekMaxReplyUserIsBannedError':
          error = new ContentValidationError('UNAUTHORIZED_REPLY');
          break;
        default:
          error = new ContentValidationError();
      }
      throw error;
    }
    return result;
  } catch (error) {
    logger.error({ error }, 'createCommentReply');
    throw error;
  }
};

export interface deleteCommentParams {
  commentId: string;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const deleteComment = async ({
  commentId,
  client = csrClient,
}: deleteCommentParams): Promise<
  DeleteSeekMaxCommentMutation['deleteSeekMaxComment']
> => {
  try {
    const variables: DeleteSeekMaxCommentMutationVariables = {
      locale: getLocale(),
      commentId,
    };

    logger.info('deleteComment', variables);

    const { data } = await client.mutate({
      mutation: DeleteSeekMaxCommentDocument,
      variables,
    });
    const result = data?.deleteSeekMaxComment;
    if (!result) {
      throw new DeleteError();
    }
    if (result.__typename === 'DeleteSeekMaxCommentFailure') {
      let error: DeleteError;
      switch (result.errors[0].__typename) {
        case 'DeleteSeekMaxCommentAlreadyDeletedError':
          error = new DeleteError('DELETED_COMMENT');
          break;
        default:
          error = new DeleteError();
      }
      throw error;
    }
    return result;
  } catch (error) {
    logger.error({ error }, 'deleteComment');
    throw error;
  }
};

export interface reportCommentParams {
  id: string;
  reason: string;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const reportComment = async ({
  id,
  reason,
  client = csrClient,
}: reportCommentParams): Promise<
  ReportSeekMaxCommentMutation['reportSeekMaxComment']
> => {
  try {
    const variables: ReportSeekMaxCommentMutationVariables = {
      locale: getLocale(),
      id,
      reason,
    };

    logger.info('reportComment', variables);

    const { data } = await client.mutate({
      mutation: ReportSeekMaxCommentDocument,
      variables,
    });
    const result = data?.reportSeekMaxComment;
    if (!result) {
      throw new ReportError();
    }
    if (result.__typename === 'ReportSeekMaxCommentFailure') {
      if (
        result.errors?.find(
          (error) =>
            error?.__typename === 'ReportSeekMaxCommentAlreadyReportedError',
        )
      ) {
        throw new ReportError('ALREADY_REPORTED');
      }
      throw new ReportError();
    }
    return result;
  } catch (error) {
    logger.error({ error }, 'reportComment');
    throw error;
  }
};

export interface reportThreadParams {
  id: string;
  reason: string;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const reportThread = async ({
  id,
  reason,
  client = csrClient,
}: reportThreadParams): Promise<
  ReportSeekMaxThreadMutation['reportSeekMaxThread']
> => {
  try {
    const variables: ReportSeekMaxThreadMutationVariables = {
      locale: getLocale(),
      id,
      reason,
    };

    logger.info('reportThread', variables);

    const { data } = await client.mutate({
      mutation: ReportSeekMaxThreadDocument,
      variables,
    });
    const result = data?.reportSeekMaxThread;
    if (!result) {
      throw new ReportError();
    }
    if (result.__typename === 'ReportSeekMaxThreadFailure') {
      if (
        result.errors?.find(
          (error) =>
            error?.__typename === 'ReportSeekMaxThreadAlreadyReportedError',
        )
      ) {
        throw new ReportError('ALREADY_REPORTED');
      }
      throw new ReportError();
    }
    return result;
  } catch (error) {
    logger.error({ error }, 'reportThread');
    throw error;
  }
};

export interface deleteThreadParams {
  id: string;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const deleteThread = async ({
  id,
  client = csrClient,
}: deleteThreadParams): Promise<
  DeleteSeekMaxThreadMutation['deleteSeekMaxThread']
> => {
  try {
    const variables: DeleteSeekMaxThreadMutationVariables = {
      locale: getLocale(),
      id,
    };

    logger.info('deleteThread', variables);

    const { data } = await client.mutate({
      mutation: DeleteSeekMaxThreadDocument,
      variables,
    });
    const result = data?.deleteSeekMaxThread;
    if (!result) {
      throw new DeleteError();
    }
    if (result.__typename === 'DeleteSeekMaxThreadFailure') {
      if (
        result.errors?.find(
          (error) =>
            error?.__typename === 'DeleteSeekMaxThreadAlreadyDeletedError',
        )
      ) {
        throw new DeleteError('DELETED_THREAD');
      }
      throw new DeleteError();
    }
    return result;
  } catch (error) {
    logger.error({ error }, 'deleteThread');
    throw error;
  }
};

export interface fetchAttachmentStatusParams {
  id: string;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const fetchAttachmentStatus = async ({
  id,
  client = csrClient,
}: fetchAttachmentStatusParams): Promise<ThreadAttachment> => {
  try {
    const variables: SeekMaxThreadAttachmentStatusQueryVariables = {
      zone: getZone(),
      id,
    };

    logger.info('fetchAttachmentStatus', variables);

    const { data } = await client.query({
      query: SeekMaxThreadAttachmentStatusDocument,
      variables,
      fetchPolicy: 'no-cache',
    });

    const attachmentStatus = data?.seekMaxThreadAttachmentStatus;
    let threadAttachment: ThreadAttachment;
    switch (attachmentStatus?.__typename) {
      case 'SeekMaxAttachmentPublished':
        threadAttachment = {
          id,
          uri: attachmentStatus.url,
          status: 'PUBLISHED',
        };
        break;
      case 'SeekMaxAttachmentFailed':
        threadAttachment = {
          id,
          failureReason: attachmentStatus.failureReason,
          status: 'FAILED',
        };
        if (attachmentStatus.failureReason === 'virusImage') {
          throw new ImageUploadError('VIRUS_SCAN_FAILED');
        }
        if (attachmentStatus.failureReason === 'moderationFailed') {
          throw new ImageUploadError('FAILED_MODERATION');
        }
        break;
      case 'SeekMaxAttachmentPending':
        threadAttachment = {
          id,
          status: 'PENDING',
        };
        break;
      default:
        threadAttachment = {
          id,
          status: 'NO_ATTACHMENT',
        };
        break;
    }
    return threadAttachment;
  } catch (error) {
    logger.error({ error }, 'fetchAttachmentStatus');
    throw error;
  }
};

export interface fetchRepliesParams {
  commentId: string;
  config: Pick<ConfigStateContext, 'locale' | 'timezone' | 'zone'>;
  cursor?: number;
  pageSize?: number;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const fetchReplies = async ({
  commentId,
  config,
  cursor = 0,
  pageSize = PAGE_SIZE,
  client = csrClient,
}: fetchRepliesParams): Promise<Reply[]> => {
  try {
    const { locale, timezone, zone } = config;
    const variables: SeekMaxRepliesQueryVariables = {
      locale,
      cursor,
      pageSize,
      zone,
      timezone,
      commentId,
    };

    logger.info('fetchReplies', variables);

    const { data } = await client.query<
      SeekMaxRepliesQuery,
      SeekMaxRepliesQueryVariables
    >({
      query: SeekMaxRepliesDocument,
      variables,
    });

    return generateReplies(data);
  } catch (error) {
    logger.error({ error }, 'fetchReplies');
    throw error;
  }
};

export interface fetchRepliesIfNeededParams {
  replyCount: number;
  commentId: string;
  config: ConfigStateContext;
  pageSize?: number;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const fetchRepliesIfNeeded = async ({
  replyCount,
  commentId,
  config,
  pageSize = PAGE_SIZE,
  client = csrClient,
}: fetchRepliesIfNeededParams): Promise<Reply[]> => {
  if (replyCount <= 0) {
    return [];
  }
  return fetchReplies({ commentId, pageSize, client, config });
};

export interface deleteReplyParams {
  replyId: string;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const deleteReply = async ({
  replyId,
  client = csrClient,
}: deleteReplyParams): Promise<
  DeleteSeekMaxReplyMutation['deleteSeekMaxReply']
> => {
  try {
    const variables: DeleteSeekMaxReplyMutationVariables = {
      locale: getLocale(),
      countryCode: getCountryCode(),
      replyId,
    };

    logger.info('deleteReply', variables);

    const { data } = await client.mutate({
      mutation: DeleteSeekMaxReplyDocument,
      variables,
    });
    const result = data?.deleteSeekMaxReply;
    if (!result) {
      throw new DeleteError();
    }
    if (result.__typename === 'DeleteSeekMaxReplyFailure') {
      let error: DeleteError;
      switch (result.errors[0].__typename) {
        case 'DeleteSeekMaxReplyAlreadyDeletedError':
          error = new DeleteError('DELETED_COMMENT');
          break;
        default:
          error = new DeleteError();
      }
      throw error;
    }
    return result;
  } catch (error) {
    logger.error({ error }, 'deleteReply');
    throw error;
  }
};

export interface reportReplyParams {
  id: string;
  reason: string;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const reportReply = async ({
  id,
  reason,
  client = csrClient,
}: reportReplyParams): Promise<
  ReportSeekMaxReplyMutation['reportSeekMaxReply']
> => {
  try {
    const variables: ReportSeekMaxReplyMutationVariables = {
      locale: getLocale(),
      input: {
        id,
        reason,
        countryCode: getCountryCode(),
      },
    };

    logger.info('reportReply', variables);

    const { data } = await client.mutate({
      mutation: ReportSeekMaxReplyDocument,
      variables,
    });
    const result = data?.reportSeekMaxReply;
    if (!result) {
      throw new ReportError();
    }
    if (result.__typename === 'ReportSeekMaxReplyFailure') {
      if (
        result.errors?.[0]?.__typename ===
        'ReportSeekMaxReplyAlreadyReportedError'
      ) {
        throw new ReportError('ALREADY_REPORTED');
      }
      throw new ReportError();
    }
    return result;
  } catch (error) {
    logger.error({ error }, 'reportReply');
    throw error;
  }
};

export interface updatePollParams {
  optionId: string;
  pollId: string;
  zone: CareerHubZones;
  voted?: boolean;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const updatePoll = async ({
  optionId,
  pollId,
  zone,
  voted = false,
  client = csrClient,
}: updatePollParams): Promise<any> => {
  try {
    const variables: SetSeekMaxThreadPollStatusMutationVariables = {
      locale: getLocale(),
      input: {
        optionId,
        pollId,
        voted,
        zone,
      },
    };

    logger.info('updatePoll', variables);
    const { data } = await client.mutate({
      mutation: SetSeekMaxThreadPollStatusDocument,
      variables,
    });

    const pollStatus = data?.setSeekMaxThreadPollStatus;
    if (!pollStatus) {
      throw new Error('Failed to update poll');
    }
    if (pollStatus.__typename === 'SeekMaxThreadPollStatusFailure') {
      throw pollStatus.errors;
    }

    return pollStatus;
  } catch (error) {
    logger.error({ error }, 'updatePoll');
    throw error;
  }
};
